import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserDataContext } from "../components/UserDataContext";
import { Container } from "react-bootstrap";
import {
  Button,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiRequest } from "../services/apiService";
import Alerts from "../components/Alerts";

const LANGUAGES_MASTER_LIST = [
  { id: 1, name: "niemiecki" },
  { id: 2, name: "angielski" },
  { id: 3, name: "hiszpański" },
  { id: 4, name: "włoski" },
  { id: 5, name: "francuski" },
  { id: 6, name: "ukraiński" },
  { id: 7, name: "rosyjski" },
  { id: 8, name: "czeski" },
  { id: 9, name: "litewski" },
];

const LEVELS = [
  { id: 1, label: "Podstawowy" },
  { id: 2, label: "Średniozaawansowany" },
  { id: 3, label: "Zaawansowany" },
];

export default function LanguageMenu() {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  useEffect(() => {
    const fetchUserLanguages = async () => {
      if (!userData?.userId) return;
      setLoadingData(true);
      try {
        const response = await apiRequest(
          `get-user-languages/${userData.userId}`,
          "GET",
        );
        console.log("get lang.: ", response);
        if (response.success && Array.isArray(response.data)) {
          setSelectedLanguages(
            response.data.map((item) => ({
              languageId: Number(item.languageId),
              level: item.level,
            })),
          );
        }
      } catch (error) {
        console.error("Błąd pobierania:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchUserLanguages();
  }, [userData]);

  const toggleLanguage = (id) => {
    setSelectedLanguages((prev) => {
      const exists = prev.find((item) => item.languageId === id);
      if (exists) {
        return prev.filter((item) => item.languageId !== id);
      } else {
        return [...prev, { languageId: id, level: "podstawowy" }];
      }
    });
  };

  const handleLevelChange = (id, newLevel) => {
    setSelectedLanguages((prev) =>
      prev.map((item) =>
        item.languageId === id ? { ...item, level: newLevel } : item,
      ),
    );
  };

  const handleSave = async () => {
    if (!userData?.userId) return;
    setIsSaving(true);
    setShowApiResponse(false);
    try {
      const payload = {
        languages: selectedLanguages.map((item) => ({
          userId: userData.userId,
          languageId: item.languageId,
          level: item.level,
        })),
      };
      console.log("languages payload: ", payload);
      const response = await apiRequest("save-languages", "POST", payload);
      console.log("lan resp.: ", response);
      setApiResponse({
        title: response.success ? "Sukces" : "Błąd",
        severity: response.success ? "success" : "warning",
        info: response.success ? "Zapisano języki w profilu." : "Błąd zapisu.",
      });
      setShowApiResponse(true);
    } catch (error) {
      setApiResponse({
        title: "Błąd",
        severity: "error",
        info: "Błąd połączenia.",
      });
      setShowApiResponse(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (!userData || Object.keys(userData).length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
        <Typography ml={2}>Ładowanie danych użytkownika...</Typography>
      </Box>
    );
  }

  return (
    <Container>
      {showApiResponse && <Alerts data={apiResponse} />}

      <Typography variant="h4" mb={4}>
        Znajomość języków obcych
      </Typography>

      <Card>
        <CardContent>
          {loadingData ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {LANGUAGES_MASTER_LIST.map((lang) => {
                const selection = selectedLanguages.find(
                  (s) => s.languageId === lang.id,
                );
                const isSelected = !!selection;

                return (
                  <Grid item xs={12} md={6} key={lang.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        borderColor: isSelected ? "primary.main" : "divider",
                        bgcolor: isSelected
                          ? "rgba(25, 118, 210, 0.04)"
                          : "transparent",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ width: "100%" }}
                      >
                        <Box
                          onClick={() => toggleLanguage(lang.id)}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            flexGrow: 1,
                            gap: 2,
                          }}
                        >
                          <CheckCircleIcon
                            color={isSelected ? "primary" : "disabled"}
                          />
                          <Typography fontWeight={isSelected ? 600 : 400}>
                            {lang.name}
                          </Typography>
                        </Box>

                        {isSelected && (
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={selection.level}
                              onChange={(e) =>
                                handleLevelChange(lang.id, e.target.value)
                              }
                            >
                              {LEVELS.map((lvl) => (
                                <MenuItem key={lvl.id} value={lvl.id}>
                                  {lvl.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          <Box mt={4} textAlign="right">
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Zapisz zmiany"
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
